import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ExternalProjectImportEntity,
  ExternalProjectImportPreview,
} from '@provision/entity-types';
import { useMemo } from 'react';
import { ProjectPreview } from 'src/components/ImportDtoolsProjectModal/project-preview';
import { usePortalioProjectImportPreview } from 'src/graphql/entities/integrations/portalio/hooks/use-portalio-project-import-preview';

export type ProjectsSummaryProps = {
  projects: { id: number; name: string }[];
  onBackClick: () => void;
  onCancelClick: () => void;
  onImportClick: () => void;
  disableImportButton?: boolean;
};

const countEntities = (records: ExternalProjectImportPreview[] | undefined) => {
  const counts = {
    Project: 0,
    Site: 0,
    Client: 0,
  };

  const bucketEntity = (entity: ExternalProjectImportEntity) => {
    counts[entity.entity] += 1;
  };

  (records || []).forEach((record) => {
    record.created?.forEach(bucketEntity);
    record.updated?.forEach(bucketEntity);
  });

  return counts;
};

export const ProjectsSummary = ({
  projects,
  onImportClick,
  onBackClick,
  onCancelClick,
  disableImportButton,
}: ProjectsSummaryProps) => {
  const projectIds = projects.map((p) => p.id);
  const projectNamesMap = useMemo(
    () => new Map(projects.map((p) => [p.id, p.name])),
    [projects],
  );
  const { records, loading, errors } = usePortalioProjectImportPreview(projectIds);

  const counts = countEntities(records);

  const content = (() => {
    if (loading) {
      return (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height="1.5rem" width="40%" />
          <Skeleton variant="rounded" height="5rem" />
          <Skeleton variant="rounded" height="5rem" />
        </Stack>
      );
    }

    if (!records?.length) {
      return (
        <Stack spacing={1}>
          <Typography variant="subtitle2">No preview available</Typography>
          <Typography variant="body2" color="text.secondary">
            Could not generate a preview for the selected projects.
          </Typography>
        </Stack>
      );
    }

    return (
      <Stack spacing={2}>
        {errors?.length ? (
          <Alert severity="warning">
            <AlertTitle sx={{ marginBottom: 0 }}>
              Failed to get following project(s)
            </AlertTitle>
            <List dense sx={{ paddingY: 0 }}>
              {(errors || []).map((e) => (
                <ListItem key={e.projectId}>
                  <ListItemText
                    primary={projectNamesMap.get(e.projectId) ?? e.projectId}
                    secondary={e.error}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        ) : null}
        {(records || []).map((r) => (
          <ProjectPreview {...r} key={r.id} />
        ))}
        <Typography variant="body2" color="text.secondary">
          {`Summary: ${counts.Site} ${counts.Site === 1 ? 'site' : 'sites'}, ${counts.Client} ${
            counts.Client === 1 ? 'client' : 'clients'
          }, ${counts.Project} ${counts.Project === 1 ? 'project' : 'projects'}`}
        </Typography>
      </Stack>
    );
  })();

  return (
    <Dialog
      open
      onClose={onCancelClick}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6">Confirm Import</Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {`Review ${projectIds.length} project(s) to import`}
          </Typography>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 3, py: 2 }}>{content}</DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button onClick={onBackClick}>Back</Button>
        <Stack direction="row" spacing={1.5}>
          <Button onClick={onCancelClick}>Cancel</Button>
          <Button
            variant="contained"
            onClick={onImportClick}
            disabled={projectIds.length === 0 || disableImportButton}
          >
            {!errors?.length ? 'Import All' : 'Import Anyway'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
